import api, {type ApiResponse, doError} from "@/axios.js";
import {defineStore} from 'pinia'
import type {AxiosError} from "axios";
import { unregisterPushNotifications } from '@/services/push';


export interface UserInterface extends RegisterInterface {
    id: number;
    first_name:string;
    last_name:string;
    created_at: string;
    email_verified_at: string;
    token?: string;
    is_admin?: number;
    meta?: {
        orders?: number;
        last_reservation?: string;
        next_reservation?: string;
    } ;
}
export interface LoginInterface {
    email: string;
    phone:string;
    password: string;
}
export interface RegisterInterface {
    email: string;
    country_code: string;
    phone:string;
    password: string;
    password_confirmation: string;
    first_name: string;
    last_name: string;
    terms?:boolean;
    privacy?:boolean;
}
export interface PasswordReset {
    email: string;
}
export interface PasswordChange extends PasswordReset{
    password: string;
    password_confirmation: string;
    password_reset_token:string;
}
export const useUserStore = defineStore('user', {
    state: () => ({
        _auth: null as UserInterface | null,
        error: null,
        _list: null as UserInterface[] | null,
    }),
    getters: {
        auth: (state) => state._auth,
        list: (state) => state._list,
    },
    actions: {
        async load(): Promise<void> {
            try {
                const response = await api.get<ApiResponse<UserInterface[]>>('/users');
                this._list = response.data.data;
            }
            catch (err: AxiosError | any) {
                doError(err);
            }
        },
        async login(credentials: LoginInterface):Promise<void> {
            try {
                const response = await api.post<ApiResponse<UserInterface>>('/login', credentials);
                this._auth = response.data.data;
                const token = response.data.data?.token;
                if (token) {
                    localStorage.setItem('token', token);
                    api.defaults.headers.Authorization = `Bearer ${token}`;
                }
            }
            catch (err: AxiosError | any) {
                doError(err);
            }
        },

        async register(registerForm: RegisterInterface) {
            try {
                const response = await api.post<ApiResponse<UserInterface>>('/register', registerForm);
                this._auth = response.data.data;

                const token = response.data.data?.token || '';
                if (token) {
                    localStorage.setItem('token', token);
                    api.defaults.headers.Authorization = `Bearer ${token}`;
                }
                return true;
            }
            catch (err: AxiosError | any) {
                doError(err);
            }
        },

        async logout(): Promise<void> {
            try {
                 // Server logout also revokes registrations bound to this access token if this request fails.
                 await unregisterPushNotifications(false).catch(() => {});
                 await api.post<ApiResponse<{message:string}>>('/logout');
                 this._auth = null;
                 localStorage.removeItem('token');
                 delete api.defaults.headers.Authorization;
            }
            catch (err: AxiosError | any) {
                doError(err);
            }
        },

        async me():Promise<void> {
            const token = localStorage.getItem('token');
            if (!token) {
                this._auth = null;
                return;
            }
            try {
                const response = await api.get('/me');
                this._auth = response.data.data;
            } catch (err) {
                this._auth = null;
                localStorage.removeItem('token');
            }
        },
        async update(user: UserInterface) {
            try {
                const response = await api.put<ApiResponse<UserInterface>>('/users/update', user);
                this._auth = response.data.data;
            } catch (err: AxiosError | any) {
                doError(err);
            }
        },
        async socialLogin(provider:string, id_token: string, profile?: object) {
            try {
                const response = await api.post<ApiResponse<UserInterface>>('/login/' + provider, {
                    id_token: id_token,
                    profile: profile
                });
                this._auth = response.data.data;
                const token: string|undefined = response.data.data?.token;
                if (token) {
                    localStorage.setItem('token', token);
                    api.defaults.headers.Authorization = `Bearer ${token}`;
                }
            } catch (err: AxiosError | any) {
                doError(err);
            }
        },
        async reset(form:PasswordReset) {
            try {
              await api.post<ApiResponse<UserInterface>>('/password/email', form);
            } catch (err: AxiosError | any) {
                doError(err);
            }
    },
        async changePassword(form:PasswordReset) {
            try {
                await api.post<ApiResponse<UserInterface>>('/password/change', form);
            } catch (err: AxiosError | any) {
                doError(err);
            }
        },
        async deleteAccount() {
            try {
                await unregisterPushNotifications(false).catch(() => {});
                await api.delete<ApiResponse<UserInterface>>('/me');
                this._auth = null;
                localStorage.removeItem('token');
                delete api.defaults.headers.Authorization;
            } catch (err: AxiosError | any) {
                doError(err);
            }
        }
    }
})
